import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import { canManageConditionTypeExclusions } from "../access";
import cobrancaConfiguracoesService from "../services/cobrancaConfiguracoes.service";

export default function ConditionTypeExclusionsPanel() {
  const [conditionTypes, setConditionTypes] = useState([]);
  const [savedConditionTypes, setSavedConditionTypes] = useState([]);
  const [conditionTypeInput, setConditionTypeInput] = useState("");
  const [inputError, setInputError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const canManage = canManageConditionTypeExclusions();

  const hasChanges = useMemo(
    () => JSON.stringify(conditionTypes) !== JSON.stringify(savedConditionTypes),
    [conditionTypes, savedConditionTypes],
  );

  const loadConditionTypes = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const configured = await cobrancaConfiguracoesService.getConditionTypesExcluidos();
      setConditionTypes(configured);
      setSavedConditionTypes(configured);
      setLoaded(true);
    } catch (err) {
      setLoaded(false);
      setError(cobrancaConfiguracoesService.getApiErrorMessage(
        err,
        "Não foi possível carregar os condition types configurados.",
      ));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConditionTypes();
  }, [loadConditionTypes]);

  const handleAddConditionType = () => {
    const code = conditionTypeInput.trim();
    if (!code) {
      setInputError("Informe um código de condition type.");
      return;
    }
    if (code.length > 50) {
      setInputError("O código pode ter até 50 caracteres.");
      return;
    }
    if (conditionTypes.some((current) => current.trim().toUpperCase() === code.toUpperCase())) {
      setInputError("Esse código já está na lista.");
      return;
    }

    setConditionTypes((current) => [...current, code]);
    setConditionTypeInput("");
    setInputError("");
    setError("");
    setSuccess("");
  };

  const handleRemoveConditionType = (codeToRemove) => {
    setConditionTypes((current) => current.filter((code) => code !== codeToRemove));
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    if (!canManage || !loaded || !hasChanges || saving) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const updated = await cobrancaConfiguracoesService.saveConditionTypesExcluidos(conditionTypes);
      setConditionTypes(updated);
      setSavedConditionTypes(updated);
      setSuccess("Lista de exclusões salva.");
    } catch (err) {
      setError(cobrancaConfiguracoesService.getApiErrorMessage(
        err,
        "Não foi possível salvar os condition types excluídos.",
      ));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)", boxShadow: "0 1px 3px rgba(31, 58, 86, 0.035)" }}>
      <Stack spacing={{ xs: 1.5, md: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box sx={{ display: "grid", placeItems: "center", width: 40, height: 40, flexShrink: 0, borderRadius: 1.5, bgcolor: "var(--ops-subtle)", border: "1px solid var(--ops-border)" }}>
            <TuneRounded sx={{ color: "var(--ops-accent)", fontSize: 21 }} />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography sx={{ color: "var(--ops-heading)", fontWeight: 850, fontSize: { xs: 16, md: 18 }, lineHeight: 1.25 }}>
              Exclusão por condition type
            </Typography>
            <Typography sx={{ color: "var(--ops-muted)", fontSize: 13, lineHeight: 1.5, mt: 0.35 }}>
              Escolha quais tipos de condição devem ficar fora da inadimplência.
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, p: { xs: 1.25, md: 1.5 }, borderRadius: 1.5, bgcolor: "var(--ops-subtle)", border: "1px solid var(--ops-border)", borderLeft: "4px solid var(--ops-accent)" }}>
          <InfoOutlined sx={{ color: "var(--ops-accent)", fontSize: 20, mt: 0.1, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ color: "var(--ops-heading)", fontSize: 12.5, fontWeight: 800, lineHeight: 1.35 }}>
              O que será desconsiderado
            </Typography>
            <Typography sx={{ color: "var(--ops-text)", fontSize: 12.5, lineHeight: 1.5, mt: 0.25 }}>
              Títulos e parcelas com qualquer código desta lista são ignorados na carga e nas análises da inadimplência.
            </Typography>
          </Box>
        </Box>

        {!canManage ? (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            Seu usuário tem acesso somente para consulta. Apenas gestores autorizados podem alterar esta lista.
          </Alert>
        ) : null}

        {error ? <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ borderRadius: 1.5 }}>{success}</Alert> : null}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "var(--ops-muted)", py: 1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 12.5 }}>Carregando códigos configurados...</Typography>
          </Stack>
        ) : loaded ? (
          <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.75 }, borderRadius: 1.75, bgcolor: "var(--ops-canvas)", border: "1px solid var(--ops-border)" }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={0.6}>
                <Box>
                  <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 800 }}>
                    Códigos desconsiderados
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${conditionTypes.length} ${conditionTypes.length === 1 ? "código" : "códigos"}`}
                  sx={{ color: "var(--ops-accent)", bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)", fontWeight: 750 }}
                />
              </Stack>

              {conditionTypes.length ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                  {conditionTypes.map((code, index) => (
                    <Chip
                      key={`${code}-${index}`}
                      label={code}
                      size="small"
                      onDelete={canManage && !saving ? () => handleRemoveConditionType(code) : undefined}
                      sx={{
                        height: 34,
                        borderRadius: 1.25,
                        bgcolor: "var(--ops-selected)",
                        color: "var(--ops-heading)",
                        border: "1px solid var(--ops-accent)",
                        fontSize: 14,
                        fontWeight: 800,
                        "& .MuiChip-label": { px: 1.25 },
                        "& .MuiChip-deleteIcon": {
                          color: "var(--ops-danger)",
                          fontSize: 18,
                          "&:hover": { color: "var(--ops-danger)" },
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 1.5, textAlign: "center", border: "1px dashed var(--ops-border)", borderRadius: 1.5, bgcolor: "var(--ops-surface)" }}>
                  <Typography sx={{ color: "var(--ops-heading)", fontSize: 12.5, fontWeight: 700 }}>
                    Nenhum código excluído
                  </Typography>
                  <Typography sx={{ color: "var(--ops-muted)", fontSize: 12, mt: 0.25 }}>
                    Adicione um condition type para removê-lo da carga e das análises.
                  </Typography>
                </Box>
              )}

              <Divider sx={{ borderColor: "var(--ops-border)" }} />

              <Box>
                <Typography sx={{ color: "var(--ops-muted)", fontSize: 12, lineHeight: 1.45, mb: 0.75 }}>
                  Informe o código exatamente como aparece nos dados de origem (ex.: FC).
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                  <TextField
                    label="Código de condition type"
                    placeholder="Ex.: FC"
                    value={conditionTypeInput}
                    onChange={(event) => {
                      setConditionTypeInput(event.target.value);
                      setInputError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleAddConditionType();
                      }
                    }}
                    helperText={inputError || "Até 50 caracteres. Espaços externos são removidos."}
                    error={Boolean(inputError)}
                    inputProps={{ maxLength: 50 }}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 360, md: 420 }, maxWidth: "100%" }}
                    disabled={!canManage || saving}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddRounded />}
                    onClick={handleAddConditionType}
                    disabled={!canManage || saving || !loaded}
                    sx={{ flexShrink: 0, minHeight: 40, px: 2, textTransform: "none", fontWeight: 750 }}
                  >
                    Adicionar código
                  </Button>
                </Stack>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
                {hasChanges ? (
                  <Typography sx={{ color: "var(--ops-warning)", fontSize: 12, fontWeight: 700 }}>
                    Alterações ainda não salvas
                  </Typography>
                ) : <Box />}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    startIcon={<ReplayRounded />}
                    onClick={() => {
                      setConditionTypes([...savedConditionTypes]);
                      setError("");
                      setSuccess("");
                    }}
                    disabled={!canManage || saving || !hasChanges}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Restaurar lista
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineRounded />}
                    onClick={() => {
                      setConditionTypes([]);
                      setSuccess("");
                      setError("");
                    }}
                    disabled={!canManage || saving || conditionTypes.length === 0}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Limpar lista
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveRounded />}
                    onClick={handleSave}
                    disabled={!canManage || !loaded || !hasChanges || saving || loading}
                    sx={{ minHeight: 36, px: 2, textTransform: "none", fontWeight: 750, whiteSpace: "nowrap" }}
                  >
                    Salvar alterações
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Button
            variant="outlined"
            startIcon={<TuneRounded />}
            onClick={loadConditionTypes}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
